/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric,
    IRequestContext
} from '@msdyn365-commerce/core';
import { CategoriesDataActions } from '@msdyn365-commerce/retail-proxy';
import { generateCacheKey } from '@msdyn365-commerce-modules/retail-actions';

/**
 * Input for get-categories data action.
 */
export class CategoriesInput implements IActionInput {
    public readonly maxItems: number;

    public readonly channelId: number;

    public readonly catalogId?: number;

    private readonly _requestContext: IRequestContext;

    private readonly _mappedToHierarchy: boolean;

    public constructor(context: IRequestContext, mappedToHierarchy: boolean, maxItems?: number) {
        this._requestContext = context;
        this._mappedToHierarchy = mappedToHierarchy;
        this.maxItems = maxItems || 250;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Required.
        this.channelId = context?.apiSettings?.channelId ? Number(context.apiSettings.channelId) : 0;
        this.catalogId = getCatalogId(context);
    }

    /**
     * Get Cache Key.
     * @returns - Cache key string.
     */
    public getCacheKey = (): string =>
        generateCacheKey(
            this._requestContext.apiSettings,
            { key: 'Top', value: this.maxItems },
            { key: 'CatalogId', value: this.catalogId }
        );

    /**
     * Get Cache type Name.
     * @returns - Cache name string.
     */
    public getCacheObjectType = (): string => (this._mappedToHierarchy ? 'CategoryHierarchy' : 'Category');

    /**
     * Get Cache type.
     * @returns - CacheType enum.
     */
    public dataCacheType = (): CacheType => 'application';
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData -- Input data.
 * @returns -- IActionInput.
 */
export const createCategoriesInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    const topItems = inputData.config?.topCategories && Number.parseInt(inputData.config.topCategories, 10);
    return new CategoriesInput(inputData.requestContext, false, topItems);
};

/**
 * Calls the Retail API and returns all the categories as a flat list.
 * @param input - Input.
 * @param ctx - Context.
 * @returns - CategoryHierarchy.
 */
export async function getCategoryAction(input: CategoriesInput, ctx: IActionContext): Promise<CategoryHierarchy[]> {
    return ((await CategoriesDataActions.getCategoriesAsync(
        {
            callerContext: ctx
        },
        input.channelId,
        input.catalogId
    )) as unknown) as CategoryHierarchy[];
}

/**
 * The getCategory Data Action.
 */
export const getCategoryActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-categories',
    action: getCategoryAction as IAction<CategoryHierarchy[]>,
    input: createCategoriesInput
});

export default getCategoryActionDataAction;
